/**
 * نیم‌رخ حجم (Volume Profile)
 * =============================
 * به‌جای «در هر زمان چقدر معامله شد»، می‌گوید «در هر **قیمت** چقدر معامله
 * شد». همان چیزی که به تحلیل‌گر نشان می‌دهد بازار سرِ کدام قیمت‌ها واقعاً
 * دست‌به‌دست شده و کدام محدوده‌ها را فقط رد کرده است.
 *
 * سه عدد مهم:
 *  • **POC** — پرحجم‌ترین قیمت؛ معمولاً آهن‌ربای قیمت است.
 *  • **ناحیه ارزش** — باریک‌ترین محدوده‌ای که ۷۰٪ حجم داخلش است.
 *  • **ناحیه کم‌حجم** — جایی که بازار سریع رد شده و معمولاً دوباره سریع رد
 *    می‌شود.
 *
 * روش توزیع: حجمِ هر کندل به نسبت، بین سطل‌هایی که دامنه‌اش را می‌پوشاند
 * پخش می‌شود — نه اینکه همه‌اش را به قیمت بسته بچسبانیم. کندلی که از
 * ۱۰۰ تا ۱۱۰ رفته، حجمش در همه آن ده واحد پخش شده، نه فقط روی ۱۱۰.
 *
 * بدون هیچ وابستگی — تا مستقیم آزمون‌پذیر باشد.
 */
import type { Candle } from './indicators'

export type Bin = {
  lo: number; hi: number; mid: number
  up: number; down: number; total: number
}

export type Profile = {
  bins: Bin[]
  max: number
  total: number
  poc: number          // قیمتِ پرحجم‌ترین سطل
  vaLow: number        // کف ناحیه ارزش
  vaHigh: number       // سقف ناحیه ارزش
  lo: number
  hi: number
}

export function volumeProfile(
  candles: Candle[], binCount = 24, valueAreaPct = 0.7,
): Profile | null {
  const rows = (candles || []).filter((c) => c
    && Number.isFinite(c.high) && Number.isFinite(c.low)
    && c.high >= c.low && c.low > 0)
  if (rows.length < 2 || binCount < 1) return null

  let lo = Infinity
  let hi = -Infinity
  for (const c of rows) { if (c.low < lo) lo = c.low; if (c.high > hi) hi = c.high }
  if (!(hi > lo)) return null

  const n = Math.max(1, Math.floor(binCount))
  const w = (hi - lo) / n
  const bins: Bin[] = Array.from({ length: n }, (_, i) => ({
    lo: lo + i * w, hi: lo + (i + 1) * w, mid: lo + (i + 0.5) * w,
    up: 0, down: 0, total: 0,
  }))

  for (const c of rows) {
    const vol = Number.isFinite(c.volume) && c.volume > 0 ? c.volume : 0
    if (!vol) continue
    const isUp = c.close >= c.open
    const a = Math.max(lo, c.low)
    const b = Math.min(hi, c.high)
    const span = b - a
    if (span <= 0) {
      // کندل بدون دامنه: همه حجمش در سطلِ خودش
      const i = Math.min(n - 1, Math.max(0, Math.floor((a - lo) / w)))
      add(bins[i], vol, isUp)
      continue
    }
    const first = Math.min(n - 1, Math.max(0, Math.floor((a - lo) / w)))
    const last = Math.min(n - 1, Math.max(0, Math.ceil((b - lo) / w) - 1))
    for (let i = first; i <= last; i++) {
      const overlap = Math.min(b, bins[i].hi) - Math.max(a, bins[i].lo)
      if (overlap > 0) add(bins[i], vol * (overlap / span), isUp)
    }
  }

  let max = 0
  let total = 0
  let pocIdx = 0
  for (let i = 0; i < n; i++) {
    total += bins[i].total
    if (bins[i].total > max) { max = bins[i].total; pocIdx = i }
  }
  if (total <= 0) return null

  // ناحیه ارزش: از POC به دو طرف، هر بار سمتی که سطل پرحجم‌تری دارد
  const target = total * Math.min(Math.max(valueAreaPct, 0.1), 1)
  let low = pocIdx
  let high = pocIdx
  let acc = bins[pocIdx].total
  while (acc < target && (low > 0 || high < n - 1)) {
    const below = low > 0 ? bins[low - 1].total : -1
    const above = high < n - 1 ? bins[high + 1].total : -1
    if (above >= below) { high += 1; acc += bins[high].total }
    else { low -= 1; acc += bins[low].total }
  }

  return {
    bins, max, total,
    poc: bins[pocIdx].mid,
    vaLow: bins[low].lo,
    vaHigh: bins[high].hi,
    lo, hi,
  }
}

function add(b: Bin, v: number, up: boolean) {
  if (up) b.up += v
  else b.down += v
  b.total += v
}
